import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tooltip,
  Upload,
} from 'antd';
import axios from 'axios';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../../assets/css/product_image.css';
import '../../assets/scss/components/product-categories.scss';
import CustomModal from '../../components/modal';
import SearchInput from '../../components/search-input';
import { export_url } from '../../configs/app-global';
import { GIFT_API_TOKEN, GIFT_PRODUCTS_URL } from '../../configs/urls';
import { Context } from '../../context/context';
import formatSortType from '../../helpers/formatSortType';
import useDidUpdate from '../../helpers/useDidUpdate';
import { addMenu, disableRefetch, setMenuData } from '../../redux/slices/menu';
import { fetchProducts } from '../../redux/slices/product';
import brandService from '../../services/brand';
import categoryService from '../../services/category';
import productService from '../../services/product';
import shopService from '../../services/shop';

const normalizeRedeemableModes = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap(normalizeRedeemableModes);
  }
  if (!value) {
    return [];
  }

  const mode = String(value).trim();
  if (mode.startsWith('[')) {
    try {
      return normalizeRedeemableModes(JSON.parse(mode));
    } catch (_) {
      return [mode.toUpperCase()];
    }
  }
  return [mode.toUpperCase()];
};

const getRedeemableLabel = (redeemable) => {
  const modes = normalizeRedeemableModes(redeemable);
  const hasOnline = modes.includes('ONLINE');
  const hasOffline = modes.includes('OFFLINE');

  if (hasOnline && hasOffline) {
    return 'Online & Offline';
  }
  if (hasOffline) {
    return 'Offline';
  }
  return 'Online';
};

const ProductCategories = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const goToEdit = (row) => {
    dispatch(
      addMenu({
        id: `product-edit`,
        url: `product/${row.uuid}`,
        name: t('edit.product'),
      })
    );
    navigate(`/product/${row.uuid}`);
  };

  // Replacing static categories with state
  const [productCategories, setProductCategories] = useState([]);

  // Homepage categories - static options
  const homepageCategories = [
    { label: 'Popular', value: 'popular' },
    { label: 'Fashion', value: 'fashion' },
    { label: 'Food', value: 'food' },
  ];

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/quickgift/category-list`
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch categories: ${response.status}`);
        }
        const data = await response.json();

        if (data.status === 'success' && Array.isArray(data.data)) {
          const formattedCategories = data.data.map((category) => ({
            label: category.name,
            value: category.name.toLowerCase(),
            id: category.id,
            image: category.image,
          }));

          // Remove duplicates based on value (lowercase category name)
          const uniqueCategories = [];
          const valueSet = new Set();

          formattedCategories.forEach((category) => {
            if (!valueSet.has(category.value)) {
              valueSet.add(category.value);
              uniqueCategories.push(category);
            }
          });

          setProductCategories(uniqueCategories);
          console.log('Fetched categories:', uniqueCategories);
        } else {
          console.error('Invalid categories data format:', data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to static categories in case of error
        setProductCategories([
          { label: 'Food', value: 'food' },
          { label: 'Hotel', value: 'hotel' },
          { label: 'Entertainment', value: 'entertainment' },
          { label: 'Others', value: 'others' },
        ]);
      }
    };

    fetchCategories();
  }, []);

  const columns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      sorter: true,
    },
    {
      title: t('image'),
      dataIndex: 'small_image',
      render: (img, row) => (
        <div style={{ position: 'relative' }}>
          <img
            src={uploadedImages[row.sku] || img}
            width={100}
            alt='GIVZO'
            style={{ borderRadius: 4 }}
            id='product_image_hover'
          />
          <Button
            type='primary'
            size='small'
            icon={<UploadOutlined />}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              background: 'rgba(24, 144, 255, 0.8)',
            }}
            onClick={() => handleUploadClick(row.sku)}
          />
        </div>
      ),
    },
    {
      title: t('name'),
      dataIndex: 'name',
    },
    {
      title: t('active'),
      dataIndex: 'active',
      render: (active, row) => {
        return (
          <Switch
            onChange={() => {
              setIsModalVisible(true);
              setUUID(row.id);
              setIsDelete(false);
            }}
            checked={active}
          />
        );
      },
    },
    {
      title: 'MaxUseCount',
      dataIndex: 'maxUseCount',
      render: (_, row) => (
        <InputNumber
          value={maxUseCountValues[row.id] ?? row.max_monthly_purchase ?? 10}
          min={0}
          onChange={(value) => handleMaxUseCountChange(value, row.id)}
        />
      ),
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      render: (percentage, row) => (
        <InputNumber
          min={0}
          value={
            inputValues[row.id] !== undefined ? inputValues[row.id] : percentage
          }
          onChange={(value) => handlePercentageChange(value, row.id)}
          onKeyDown={(e) => {
            if (e.key === '-' || e.key === 'e') e.preventDefault();  
          }}

          onFocus={() => handleFocus(row.id)}
          formatter={(value) => `${value}%`}
          parser={(value) => value.replace('%', '')}
        />
      ),
    },
    {
      title: 'Monthly Purchase Limit',
      dataIndex: 'monthly_purchase_limit',
      render: (_, row) => (
        <InputNumber
          value={
            monthlyPurchaseValues[row.id] ?? row.monthly_purchase_limit ?? 8000
          }
          min={0}
          onChange={(value) => handleMonthlyPurchaseChange(value, row.id)}
        />
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (_, row) => (
        <Select
          style={{ width: 120 }}
          value={categoryValues[row.sku] ?? row.category ?? undefined}
          onChange={(value) => handleCategoryChange(value, row.sku)}
          options={productCategories}
          placeholder='Select category'
        />
      ),
    },
    {
      title: 'Redeemable',
      dataIndex: 'product_redeemable',
      render: getRedeemableLabel,
    },
    {
      title: () => (
        <Tooltip title='Assigns products to special sections on the homepage'>
          <span>Homepage Category</span>
        </Tooltip>
      ),
      dataIndex: 'homepage_category',
      render: (_, row) => (
        <Select
          style={{ width: 120 }}
          value={homepageCategoryValues[row.sku] ?? undefined}
          onChange={(value) => handleHomepageCategoryChange(value, row.sku)}
          options={homepageCategories}
          placeholder='Select homepage category'
          allowClear
        />
      ),
    },
  ];

  const [catalogProducts, setCatalogProducts] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [maxUseCountValues, setMaxUseCountValues] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [monthlyPurchaseValues, setMonthlyPurchaseValues] = useState({});
  const [categoryValues, setCategoryValues] = useState(() => {
    // Try to get stored category values from localStorage
    const storedCategories = localStorage.getItem('productCategories');
    return storedCategories ? JSON.parse(storedCategories) : {};
  });
  const [homepageCategoryValues, setHomepageCategoryValues] = useState(() => {
    // Try to get stored homepage category values from localStorage
    const storedHomepageCategories = localStorage.getItem(
      'productHomepageCategories'
    );
    return storedHomepageCategories ? JSON.parse(storedHomepageCategories) : {};
  });
  const [currentSku, setCurrentSku] = useState(null);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState(() => {
    // Try to get stored image URLs from localStorage
    const storedImages = localStorage.getItem('productImages');
    return storedImages ? JSON.parse(storedImages) : {};
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const fetchCatalogProducts = async () => {
      try {
        console.log('Fetching catalog products...');

        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/quickgift/all-products`
        );

        console.log('Catelog products response:', response.body);

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.status}`);
        }
        const body = await response.json();
        const data = body.data;
        // Initialize values for maxUseCount, monthly purchase limit, and category
        const initialMaxUseValues = {};
        const initialMonthlyValues = {};
        const initialCategoryValues = {};
        const initialHomepageCategoryValues = {};

        // Get stored categories from localStorage
        const storedCategories = localStorage.getItem('productCategories');
        const savedCategories = storedCategories
          ? JSON.parse(storedCategories)
          : {};

        // Get stored homepage categories from localStorage
        const storedHomepageCategories = localStorage.getItem(
          'productHomepageCategories'
        );
        const savedHomepageCategories = storedHomepageCategories
          ? JSON.parse(storedHomepageCategories)
          : {};

        // Get stored images from localStorage
        const storedImages = localStorage.getItem('productImages');
        const savedImages = storedImages ? JSON.parse(storedImages) : {};

        console.log('Data:===========>', data);

        // Apply stored images to products where available
        const productsWithStoredImages = data.map((product) => {
          if (savedImages[product.sku]) {
            return {
              ...product,
              small_image: savedImages[product.sku],
            };
          }
          return product;
        });

        data.forEach((product) => {
          initialMaxUseValues[product.id] =
            product.max_monthly_purchase ?? 1000;
          initialMonthlyValues[product.id] =
            product.monthly_purchase_limit ?? 8000;

          // Use stored category if available, otherwise use product's category
          // Ensure it matches one of our dynamically loaded categories
          initialCategoryValues[product.sku] =
            savedCategories[product.sku] ||
            (product.category ? product.category.toLowerCase() : undefined);

          // Use stored homepage category if available
          initialHomepageCategoryValues[product.sku] =
            savedHomepageCategories[product.sku] || product.homepage_category;
        });

        // Fetch homepage products to get current homepage categories
        try {
          const homepageResponse = await fetch(
            `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/homepage-products`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );

          if (homepageResponse.ok) {
            const homepageData = await homepageResponse.json();

            // Map homepage categories to products
            if (
              homepageData &&
              homepageData.popular &&
              Array.isArray(homepageData.popular)
            ) {
              homepageData.popular.forEach((product) => {
                if (product.sku) {
                  initialHomepageCategoryValues[product.sku] = 'popular';
                }
              });
            }

            if (
              homepageData &&
              homepageData.fashion &&
              Array.isArray(homepageData.fashion)
            ) {
              homepageData.fashion.forEach((product) => {
                if (product.sku) {
                  initialHomepageCategoryValues[product.sku] = 'fashion';
                }
              });
            }

            if (
              homepageData &&
              homepageData.food &&
              Array.isArray(homepageData.food)
            ) {
              homepageData.food.forEach((product) => {
                if (product.sku) {
                  initialHomepageCategoryValues[product.sku] = 'food';
                }
              });
            }
          }
        } catch (error) {
          console.error('Error fetching homepage products:', error);
        }

        setMaxUseCountValues(initialMaxUseValues);
        setMonthlyPurchaseValues(initialMonthlyValues);
        setCategoryValues((prevCategories) => ({
          ...prevCategories,
          ...initialCategoryValues,
        }));
        setHomepageCategoryValues((prevHomepageCategories) => ({
          ...prevHomepageCategories,
          ...initialHomepageCategoryValues,
        }));
        setCatalogProducts(productsWithStoredImages);
      } catch (error) {
        console.error('Error fetching catalog products:', error);
      }
    };

    fetchCatalogProducts();
  }, []);

  // Helper function to find the correct category value for a product
  const findCategoryValue = useCallback(
    (productCategory) => {
      if (!productCategory) return undefined;

      // First try to match directly with the category value
      const categoryLower = productCategory.toLowerCase();
      const match = productCategories.find((c) => c.value === categoryLower);
      if (match) return match.value;

      // If no direct match, try to find a category with similar name
      const similarMatch = productCategories.find(
        (c) =>
          c.value.includes(categoryLower) || categoryLower.includes(c.value)
      );
      if (similarMatch) return similarMatch.value;

      // If no match at all, return the first category or undefined
      return productCategories.length > 0
        ? productCategories[0].value
        : undefined;
    },
    [productCategories]
  );

  // Ensure category values are updated when productCategories are loaded
  useEffect(() => {
    if (productCategories.length > 0 && catalogProducts.length > 0) {
      const updatedCategories = {};

      catalogProducts.forEach((product) => {
        const currentValue = categoryValues[product.sku];
        // If current value isn't in our categories list, find a matching one
        if (
          currentValue &&
          !productCategories.some((c) => c.value === currentValue)
        ) {
          updatedCategories[product.sku] = findCategoryValue(currentValue);
        } else if (!currentValue && product.category) {
          // If no value is set but product has a category, find a matching one
          updatedCategories[product.sku] = findCategoryValue(product.category);
        }
      });

      if (Object.keys(updatedCategories).length > 0) {
        setCategoryValues((prev) => ({
          ...prev,
          ...updatedCategories,
        }));
      }
    }
  }, [productCategories, catalogProducts, categoryValues, findCategoryValue]);

  // Save category values to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('productCategories', JSON.stringify(categoryValues));
  }, [categoryValues]);

  // Save homepage category values to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(
      'productHomepageCategories',
      JSON.stringify(homepageCategoryValues)
    );
  }, [homepageCategoryValues]);

  // Save uploaded images to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('productImages', JSON.stringify(uploadedImages));
  }, [uploadedImages]);

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const filteredData = catalogProducts.filter((item) => {
    const nameMatch = item.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    // console.log('Filtering item:', {
    //   item,
    //   nameMatch,
    //   searchTerm,
    //   totalProducts: catalogProducts.length,
    // });
    return nameMatch;
  });

  const [uuid, setUUID] = useState(false);
  const { setIsModalVisible } = useContext(Context);
  const [isDelete, setIsDelete] = useState(false);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [giftapiproducts, setGiftProducts] = useState(null);
  const { activeMenu } = useSelector((state) => state.menu, shallowEqual);
  const { products, meta, loading, params } = useSelector(
    (state) => state.product,
    shallowEqual
  );

  const [productsLoading, setProductsLoading] = useState(false);

  //! Imposter function
  const fetchgiftproducts = async () => {
    setProductsLoading(true);
    let authtoken = localStorage.getItem(GIFT_API_TOKEN);
    await axios.get(GIFT_PRODUCTS_URL + authtoken).then((response) => {
      let giftproducts = response.data.data;

      let data = [];
      giftproducts.map((item, index) => {
        let model = {
          id: index,
          name: item.name,
          price: item.currency.symbol + item.minPrice,
          active: true,
          category_name: 'Demo Category',
          img:
            item.images.base !== ''
              ? item.images.base
              : 'https://api.givzo.in/storage/images/users/1-1668423628.png',
        };
        data.push(model);
      });
      setGiftProducts(data);
      setProductsLoading(false);
    });
  };

  const goToImport = () => {
    dispatch(
      addMenu({
        id: 'product-import',
        url: `catalog/product/import`,
        name: t('product.import'),
      })
    );
    navigate(`/catalog/product/import`);
  };

  const productDelete = () => {
    setLoadingBtn(true);
    productService
      .delete(uuid)
      .then(() => {
        setIsModalVisible(false);
        toast.success(t('successfully.deleted'));
        dispatch(fetchProducts(params));
      })
      .finally(() => setLoadingBtn(false));
  };

  const handleActive = () => {
    setLoadingBtn(true);
    productService
      .setActive(uuid)
      .then(() => {
        setCatalogProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.id === uuid
              ? { ...product, active: !product.active }
              : product
          )
        );
        setIsModalVisible(false);
        toast.success(t('successfully.updated'));
      })
      .finally(() => setLoadingBtn(false));
  };

  const debounceTimeout = useRef(null);

  const handlePercentageChange = (value, id) => {
    if (value === undefined || value === '') {
      value = 0;
    }

    setInputValues((prevValues) => ({
      ...prevValues,
      [id]: value,
    }));

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      updatePercentage(id, value);
    }, 2000);
  };

  const handleMaxUseCountChange = (value, id) => {
    const newValue = value === undefined || value === '' ? 10 : value;

    setMaxUseCountValues((prevValues) => ({
      ...prevValues,
      [id]: newValue,
    }));

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      updateMaxUseCount(id, newValue);
    }, 2000);
  };

  const handleBlurMaxUseCount = (id) => {
    const maxUseCount = maxUseCountValues[id];
    if (maxUseCount !== undefined) {
      updateMaxUseCount(id, maxUseCount);
    }
  };

  const updateMaxUseCount = (uuid, maxcount) => {
    setLoadingBtn(true);
    productService
      .updateMaxUseCount(uuid, maxcount)
      .then(() => {
        setCatalogProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.id === uuid
              ? { ...product, max_monthly_purchase: maxcount }
              : product
          )
        );
        setMaxUseCountValues((prev) => ({
          ...prev,
          [uuid]: maxcount,
        }));
        toast.success('Successfully updated max use count');
      })
      .catch((error) => {
        const originalValue =
          catalogProducts.find((p) => p.id === uuid)?.max_monthly_purchase ??
          10;
        setMaxUseCountValues((prev) => ({
          ...prev,
          [uuid]: originalValue,
        }));
        toast.error('Failed to update max use count');
        console.error(error);
      })
      .finally(() => setLoadingBtn(false));
  };

  const handleFocus = (id) => {
    setInputValues((prevValues) => ({
      ...prevValues,
      [id]: '',
    }));
  };

  const updatePercentage = (uuid, percentage) => {
    setLoadingBtn(true);
    productService
      .updatePercentage(uuid, percentage)
      .then(() => {
        setCatalogProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.id === uuid ? { ...product, percentage } : product
          )
        );
        toast.success('Successfully updated percentage');
      })
      .finally(() => setLoadingBtn(false));
  };

  function onChangePagination(pagination, filters, sorter) {
    const { pageSize: perPage, current: page } = pagination;
    const { field: column, order } = sorter;
    const sort = formatSortType(order);
    dispatch(
      setMenuData({ activeMenu, data: { perPage, page, column, sort } })
    );
  }

  useDidUpdate(() => {
    const data = activeMenu.data;
    const paramsData = {
      search: data?.search,
      brand_id: data?.brand?.value,
      category_id: data?.category?.value,
      shop_id: data?.shop?.value,
      sort: data?.sort,
      column: data?.column,
      perPage: data?.perPage,
      page: data?.page,
    };
    dispatch(fetchProducts(paramsData));
  }, [activeMenu.data]);

  useEffect(() => {
    if (activeMenu.refetch) {
      dispatch(fetchProducts());
      dispatch(disableRefetch(activeMenu));
    }
  }, [activeMenu.refetch]);

  const excelExport = () => {
    setDownloading(true);
    const body = {
      shop_id: activeMenu.data.shop.value,
    };
    productService
      .export(body)
      .then((res) => {
        const body = export_url + res.data.file_name;
        window.location.href = body;
      })
      .finally(() => setDownloading(false));
  };

  const goToAddProduct = () => {
    dispatch(
      addMenu({
        id: 'product-add',
        url: 'product/add',
        name: t('add.product'),
      })
    );
    navigate('/product/add');
  };

  async function fetchBrands(search) {
    return brandService.search(search).then(({ data }) =>
      data.map((item) => ({
        label: item.title,
        value: item.id,
      }))
    );
  }

  async function fetchCategories(search) {
    const params = { search };
    return categoryService.search(params).then(({ data }) =>
      data.map((item) => ({
        label: item.translation?.title,
        value: item.id,
      }))
    );
  }

  async function fetchShops(search) {
    const params = { search };
    return shopService.search(params).then(({ data }) =>
      data.map((item) => ({
        label: item.translation?.title,
        value: item.id,
      }))
    );
  }

  useEffect(() => {
    if (localStorage.getItem(GIFT_API_TOKEN) !== null) {
      fetchgiftproducts();
    }
  }, []);

  const handleMonthlyPurchaseChange = (value, id) => {
    const newValue = value === undefined || value === '' ? 8000 : value;

    setMonthlyPurchaseValues((prevValues) => ({
      ...prevValues,
      [id]: newValue,
    }));

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      updateMonthlyPurchase(id, newValue);
    }, 2000);
  };

  const updateMonthlyPurchase = (uuid, limit) => {
    setLoadingBtn(true);
    axios
      .post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/products/${uuid}/purchase/limit/${limit}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      )
      .then((response) => {
        if (response.status === 200) {
          setCatalogProducts((prevProducts) =>
            prevProducts.map((product) =>
              product.id === uuid
                ? { ...product, monthly_purchase_limit: limit }
                : product
            )
          );
          setMonthlyPurchaseValues((prev) => ({
            ...prev,
            [uuid]: limit,
          }));
          toast.success('Successfully updated monthly purchase limit');
        } else {
          throw new Error('Failed to update monthly purchase limit');
        }
      })
      .catch((error) => {
        const originalValue =
          catalogProducts.find((p) => p.id === uuid)?.monthly_purchase_limit ??
          8000;
        setMonthlyPurchaseValues((prev) => ({
          ...prev,
          [uuid]: originalValue,
        }));
        toast.error(
          error.response?.data?.message ||
          'Failed to update monthly purchase limit'
        );
        console.error(
          'Error updating monthly purchase limit:',
          error.response?.data || error.message
        );
      })
      .finally(() => setLoadingBtn(false));
  };

  const handleCategoryChange = (value, sku) => {
    setCategoryValues((prevValues) => {
      const newValues = {
        ...prevValues,
        [sku]: value,
      };
      return newValues;
    });

    // Find the selected category object to get the cat_id
    const selectedCategory = productCategories.find(
      (cat) => cat.value === value
    );
    if (selectedCategory) {
      updateCategory(sku, value, selectedCategory.id);
    } else {
      console.error('Category not found for value:', value);
    }
  };

  const updateCategory = (sku, categoryValue, categoryId) => {
    if (!categoryValue) {
      console.error('Category value is empty');
      return;
    }

    setLoadingBtn(true);

    axios
      .post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/products/${sku}/update/${categoryId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      )
      .then((response) => {
        if (response.status === 200) {
          setCatalogProducts((prevProducts) => {
            const updatedProducts = prevProducts.map((product) =>
              product.sku === sku
                ? { ...product, category: categoryValue }
                : product
            );
            return updatedProducts;
          });

          setCategoryValues((prev) => {
            const newValues = {
              ...prev,
              [sku]: categoryValue,
            };
            // Save to localStorage immediately after successful update
            localStorage.setItem(
              'productCategories',
              JSON.stringify(newValues)
            );
            return newValues;
          });
          toast.success('Successfully updated category');
        } else {
          throw new Error('Failed to update category');
        }
      })
      .catch((error) => {
        console.error('API Error:', error);
        const originalValue = catalogProducts.find(
          (p) => p.sku === sku
        )?.category;
        setCategoryValues((prev) => {
          const newValues = {
            ...prev,
            [sku]: originalValue,
          };
          // Update localStorage with original value on error
          localStorage.setItem('productCategories', JSON.stringify(newValues));
          return newValues;
        });
        toast.error(
          error.response?.data?.message || 'Failed to update category'
        );
      })
      .finally(() => {
        setLoadingBtn(false);
        // Refresh the product list to ensure we have the latest data
        const fetchCatalogProducts = async () => {
          try {
            const response = await fetch(
              `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/quickgift/all-products`
            );

            if (!response.ok) {
              throw new Error(`Failed to fetch data: ${response.status}`);
            }
            const data = await response.json();

            // Get stored images from localStorage
            const storedImages = localStorage.getItem('productImages');
            const savedImages = storedImages ? JSON.parse(storedImages) : {};

            // Apply stored images to products where available
            const productsWithStoredImages = data.map((product) => {
              if (savedImages[product.sku]) {
                return {
                  ...product,
                  small_image: savedImages[product.sku],
                };
              }
              return product;
            });

            setCatalogProducts(productsWithStoredImages);
          } catch (error) {
            console.error('Error refreshing products:', error);
          }
        };
        fetchCatalogProducts();
      });
  };

  const handleHomepageCategoryChange = (value, sku) => {
    setHomepageCategoryValues((prevValues) => {
      const newValues = {
        ...prevValues,
        [sku]: value,
      };
      return newValues;
    });

    updateHomepageCategory(sku, value);
  };

  const updateHomepageCategory = (sku, categoryValue) => {
    if (!sku) {
      console.error('SKU is empty');
      return;
    }

    setLoadingBtn(true);
    // Using category value for the API
    console.log('Sending Homepage Category API request:', {
      sku,
      categoryValue,
    });

    // If categoryValue is null or undefined, remove the product from homepage
    if (!categoryValue) {
      axios
        .delete(
          `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/homepage-products/remove/${sku}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        )
        .then((response) => {
          console.log('Homepage Category Remove API Response:', response);
          if (response.status === 200) {
            setCatalogProducts((prevProducts) => {
              const updatedProducts = prevProducts.map((product) =>
                product.sku === sku
                  ? { ...product, homepage_category: null }
                  : product
              );
              console.log(
                'Updated Products after homepage category removal:',
                updatedProducts
              );
              return updatedProducts;
            });

            setHomepageCategoryValues((prev) => {
              const newValues = { ...prev };
              delete newValues[sku]; // Remove the entry completely
              // Save to localStorage immediately after successful update
              localStorage.setItem(
                'productHomepageCategories',
                JSON.stringify(newValues)
              );
              return newValues;
            });
            toast.success('Successfully removed from homepage');
          } else {
            throw new Error('Failed to remove from homepage');
          }
        })
        .catch((error) => {
          console.error('Homepage Category Remove API Error:', error);
          toast.error(
            error.response?.data?.message || 'Failed to remove from homepage'
          );
        })
        .finally(() => {
          setLoadingBtn(false);
        });
      return;
    }

    // Prepare request data for adding/updating
    const requestData = {
      product_cat: categoryValue,
    };

    axios
      .put(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/homepage-products/update/${sku}`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      )
      .then((response) => {
        console.log('Homepage Category API Response:', response);
        if (response.status === 200) {
          setCatalogProducts((prevProducts) => {
            const updatedProducts = prevProducts.map((product) =>
              product.sku === sku
                ? { ...product, homepage_category: categoryValue }
                : product
            );
            console.log(
              'Updated Products with Homepage Category:',
              updatedProducts
            );
            return updatedProducts;
          });

          setHomepageCategoryValues((prev) => {
            const newValues = {
              ...prev,
              [sku]: categoryValue,
            };
            // Save to localStorage immediately after successful update
            localStorage.setItem(
              'productHomepageCategories',
              JSON.stringify(newValues)
            );
            return newValues;
          });
          toast.success('Successfully updated homepage category');
        } else {
          throw new Error('Failed to update homepage category');
        }
      })
      .catch((error) => {
        console.error('Homepage Category API Error:', error);
        const originalValue = homepageCategoryValues[sku];
        setHomepageCategoryValues((prev) => {
          const newValues = {
            ...prev,
            [sku]: originalValue,
          };
          // Update localStorage with original value on error
          localStorage.setItem(
            'productHomepageCategories',
            JSON.stringify(newValues)
          );
          return newValues;
        });
        toast.error(
          error.response?.data?.message || 'Failed to update homepage category'
        );
      })
      .finally(() => {
        setLoadingBtn(false);
      });
  };

  const handleUploadClick = (sku) => {
    setCurrentSku(sku);
    setSelectedFile(null);
    setPreviewImage(null);
    setIsUploadModalVisible(true);
  };

  const handleUploadCancel = () => {
    setIsUploadModalVisible(false);
    setSelectedFile(null);
    setPreviewImage(null);
  };

  const handleFileSelect = async (file) => {
    if (file) {
      setSelectedFile(file);
      // Preview the selected image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
    return false; // Prevent automatic upload
  };

  const handleImageUpload = async () => {
    if (!selectedFile || !currentSku) {
      message.error('Please select an image file');
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/quickgift/products/${currentSku}/upload`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      console.log('Upload response:', data);

      // Update the image in the UI
      setUploadedImages((prev) => ({
        ...prev,
        [currentSku]: data.image_url,
      }));

      // Update the product data
      setCatalogProducts((prev) =>
        prev.map((product) =>
          product.sku === currentSku
            ? { ...product, small_image: data.image_url }
            : product
        )
      );

      toast.success('Image uploaded successfully');
      setIsUploadModalVisible(false);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadLoading(false);
    }
  };

  if (productsLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh', // full screen height
          width: '100%', // full width
        }}
      >
        <Spin size='large' />
      </div>
    );
  }
  return (
    <Card
      title={t('Gift Card Products')}
      extra={
        <Space>
          <Button>{t('Sync')}</Button>
        </Space>
      }
    >
      <div className='d-flex justify-content-end mb-2'>
        <SearchInput handleChange={handleSearch} />
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey={(record) => record.id}
        onChange={onChangePagination}
      />

      <CustomModal
        click={isDelete ? productDelete : handleActive}
        text={isDelete ? t('delete.product') : t('set.active.product')}
        loading={loadingBtn}
      />

      {/* Image Upload Modal */}
      <Modal
        title='Upload Product Image'
        open={isUploadModalVisible}
        onCancel={handleUploadCancel}
        footer={[
          <Button key='back' onClick={handleUploadCancel}>
            Cancel
          </Button>,
          <Button
            key='submit'
            type='primary'
            loading={uploadLoading}
            onClick={handleImageUpload}
            disabled={!selectedFile}
          >
            Upload
          </Button>,
        ]}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: 16 }}>
            <Upload
              beforeUpload={handleFileSelect}
              showUploadList={false}
              accept='.jpg,.jpeg,.png,.webp'
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select Image</Button>
            </Upload>
            <div style={{ marginTop: 16 }}>
              {previewImage ? (
                <Card style={{ width: '100%' }}>
                  <img
                    src={previewImage}
                    alt='Preview'
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      display: 'block',
                      margin: '0 auto',
                    }}
                  />
                </Card>
              ) : (
                <div
                  style={{
                    border: '1px dashed #d9d9d9',
                    borderRadius: '4px',
                    padding: '40px 20px',
                    background: '#fafafa',
                    textAlign: 'center',
                  }}
                >
                  <p>Preview will appear here</p>
                </div>
              )}
            </div>
          </div>
          {uploadLoading && (
            <div style={{ marginTop: 16 }}>
              <Spin tip='Uploading...' />
            </div>
          )}
        </div>
      </Modal>
    </Card>
  );
};

export default ProductCategories;
